import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { isEmpty } from 'lodash';
import { imageUrl, shortenandHoverString, resolveDynamicColumnDisplay } from '../../../../Components/helper';
import noColorImage from '../../../../assets/images/color-noimage.svg';
// import noImage from "../../../../assets/images/noimage.svg";
import noImage from '../../../../assets/images/RPNoImage.png';

import './RenderProductInformationColouredCard.css';
import { useProductLandingPath } from '../../../../Components/CustomHooks/useProductLandingPath';

function RenderProductInformationColouredCard({
  productsList,
  selectedCardAttributes = [],
  colouredFaces,
  handleSelection,
  selectedIds = [],
  handleCombinationSelection,
  selectedCombinations = [],
}) {
  const { t } = useTranslation('common');
  const productLandingPath = useProductLandingPath();
  if (!productsList || productsList.length === 0) {
    return (
      <div className="text-center py-4">
        <p>{t('RANGE_PLANS.PRODUCT_LIST.NO_RECORDS_FOUND')}</p>
      </div>
    );
  }
  const imgSrc = (element) => {
    if (!element?.image || isEmpty(element?.image) || element?.image === null) {
      return noImage;
    }
    if (element.image.includes('https://') || element.image.includes('http://')) {
      return element.image;
    }
    return `${imageUrl}/${element.image}`;
  };

  const getColourwayDesignImageSrc = (combination) => {
    if (!combination?.designImages?.length) return null;
    for (const design of combination.designImages) {
      if (design.isArchived) continue;
      const slot = design.images?.find((img) => img?.image);
      if (slot?.image) return slot.image;
    }
    return null;
  };

  // Function to get the value for a specific attribute
  const getAttributeValue = (product, attribute, combination = null) => {
    switch (attribute.value) {
      case 'number':
        return shortenandHoverString(product?.number || '--', 20);
      case 'description':
        return shortenandHoverString(product?.description || '--', 30);
      case 'brand':
        return product?.brandId?.name || '--';
      case 'subBrand':
        return product?.subBrandId?.name || '--';
      case 'season':
        return product?.seasonId?.name || '--';
      case 'month':
        return product?.month || '--';
      case 'year':
        return product?.year || '--';
      case 'productType':
        return product?.supplierType?.name || '--';
      case 'category1':
        return product?.catOneId?.name || '--';
      case 'category2':
        return product?.catTwoId?.name || '--';
      case 'category3':
        return product?.catThreeId?.name || '--';
      case 'category4':
        return product?.catFourId?.name || '--';
      case 'status':
        return product?.status?.name || '--';
      case 'colourway':
        if (combination?.name) {
          return combination.name;
        }
        return product?.colourLibraries?.map((data) => data?.name)?.join(', ') || '--';
      case 'supplier':
        return product?.suppliers?.join(', ') || '--';
      case 'factory':
        return product?.supplierFactories?.map((data) => data?.factoryName)?.join(', ') || '--';
      case 'licensor':
        return product?.licensorId?.name || '--';
      case 'license':
        return product?.licenseId?.name || '--';
      case 'likeness':
        return product?.likenessId?.name || '--';
      default:
        // Handle custom attributes
        if (attribute.value.startsWith('custom_')) {
          const customId = attribute.value.replace('custom_', '');
          const matchingColumn = product?.dynamicColumn?.find((dynCol) => dynCol?.colId?._id === customId);
          if (matchingColumn?.valueId || matchingColumn?.valueText) {
            return shortenandHoverString(resolveDynamicColumnDisplay(matchingColumn), 20);
          }
        }
        return '--';
    }
  };
  // Function to render a single card
  const renderCard = (product, combination = null, cardKey, showColourLibraryCount = false) => {
    const isCombinationView = colouredFaces && combination;
    const isSelected = isCombinationView ? selectedCombinations?.includes(`${product._id}_${combination._id}`) : selectedIds?.includes(product._id);

    const handleCheckboxChange = () => {
      if (isCombinationView) {
        handleCombinationSelection(product._id, combination._id);
      } else {
        handleSelection(product._id);
      }
    };

    const colourwayImgSrc = combination?.image ? `${imageUrl}/${combination.image}` : noColorImage;
    const colourLibraryCount = product?.colourLibraries?.length || 0;
    const designImageSrc = isCombinationView ? getColourwayDesignImageSrc(combination) : null;

    const mainImageSrc = designImageSrc || imgSrc(product);
    return (
      <div key={cardKey} className="rp-product-info-card card mt-0 mb-0">
        <div className="rp-card-header">
          <div className="rp-card-checkbox">
            <div className="common-checkbox">
              <input
                type="checkbox"
                id={`card_checkbox_${cardKey}`}
                checked={isSelected}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
              />
              <label htmlFor={`card_checkbox_${cardKey}`} className="chklabel" />
            </div>
          </div>

          {isCombinationView && (
            <div className="rp-colourway-image-container">
              <div
                className="rp-colourway-image"
                style={{
                  backgroundImage: `url(${colourwayImgSrc})`,
                  opacity: combination?.image ? 0.6 : 1,
                }}
                title={combination.name}
              />
            </div>
          )}

          {showColourLibraryCount && colourLibraryCount > 1 && <div className="rp-colour-library-count-badge">{colourLibraryCount}</div>}

          <div className="rp-image-detail">
            <div className="rp-card-image-preview" style={{ backgroundImage: `url(${mainImageSrc})` }} />
            <span className="rp-big-image" style={{ backgroundImage: `url(${mainImageSrc})` }} />
          </div>
        </div>
        <div className="rp-card-body">
          {/* Dynamic Attributes List */}
          <ul className="rp-card-ul">
            {(() => {
              const productNumberAttr = { value: 'number', label: 'Product Number' };
              const userSelectedAttrs = selectedCardAttributes.filter((attr) => attr.value !== 'number' && attr.value !== 'subBrand');
              const allAttributesToShow = [productNumberAttr, ...userSelectedAttrs];

              return allAttributesToShow.map((attribute, attrIndex) => {
                if (attribute.isGroup && attribute.children) {
                  const groupValues = attribute.children
                    .filter((child) => child.value !== 'subBrand')
                    .map((child) => {
                      const value = getAttributeValue(product, child);
                      return value !== '--' ? value : null;
                    })
                    .filter((v) => v !== null);

                  if (groupValues.length === 0) {
                    return <li key={`${attribute.value}_${attrIndex}`}>{attrIndex === 0 ? <strong className="text-bold">--</strong> : '--'}</li>;
                  }

                  const displayValue = groupValues.join(', ');
                  return (
                    <li key={`${attribute.value}_${attrIndex}`}>{attrIndex === 0 ? <strong className="text-bold">{displayValue}</strong> : displayValue}</li>
                  );
                }

                const value = getAttributeValue(product, attribute);
                if (attribute.value === 'number' && product?._id) {
                  return (
                    <li key={`${attribute.value}_${attrIndex}`}>
                      {attrIndex === 0 ? (
                        <strong className="text-bold">
                          <Link
                            target="_blank"
                            to={productLandingPath(product._id)}
                            style={{ textDecoration: 'underline', color: 'inherit' }}
                          >
                            {value}
                          </Link>
                        </strong>
                      ) : (
                        <Link
                          target="_blank"
                          to={productLandingPath(product._id)}
                          style={{ textDecoration: 'underline', color: 'inherit' }}
                        >
                          {value}
                        </Link>
                      )}
                    </li>
                  );
                }
                return <li key={`${attribute.value}_${attrIndex}`}>{attrIndex === 0 ? <strong className="text-bold">{value}</strong> : value}</li>;
              });
            })()}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1440: 5 }}>
      <Masonry className="custom-masonry" gutter="30px">
        {productsList?.map((product) => {
          // When colouredFaces is true, loop through combinations
          if (colouredFaces) {
            // If combinationData exists and has items, render card for each combination
            if (product.combinationData && Array.isArray(product.combinationData) && product.combinationData.length > 0) {
              return product.combinationData.map((combination) => renderCard(product, combination, `${product._id}_${combination._id}`, false));
            }
            // If no combinations, render single card
            return renderCard(product, null, `${product._id}`, false);
          }
          // When colouredFaces is false, render single card with colour library count
          return renderCard(product, null, product._id, true);
        })}
      </Masonry>
    </ResponsiveMasonry>
  );
}

RenderProductInformationColouredCard.propTypes = {
  productsList: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      image: PropTypes.string,
      number: PropTypes.string,
      description: PropTypes.string,
      brandId: PropTypes.shape({
        name: PropTypes.string,
      }),
      subBrandId: PropTypes.shape({
        name: PropTypes.string,
      }),
      seasonId: PropTypes.shape({
        name: PropTypes.string,
      }),
      month: PropTypes.string,
      year: PropTypes.string,
      supplierType: PropTypes.shape({
        name: PropTypes.string,
      }),
      catOneId: PropTypes.shape({
        name: PropTypes.string,
      }),
      catTwoId: PropTypes.shape({
        name: PropTypes.string,
      }),
      catThreeId: PropTypes.shape({
        name: PropTypes.string,
      }),
      catFourId: PropTypes.shape({
        name: PropTypes.string,
      }),
      status: PropTypes.shape({
        name: PropTypes.string,
      }),
      colourLibraries: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        }),
      ),
      suppliers: PropTypes.arrayOf(PropTypes.string),
      supplierFactories: PropTypes.arrayOf(
        PropTypes.shape({
          factoryName: PropTypes.string,
        }),
      ),
      combinationData: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          name: PropTypes.string,
          image: PropTypes.string,
        }),
      ),
      dynamicColumn: PropTypes.arrayOf(
        PropTypes.shape({
          colId: PropTypes.shape({
            _id: PropTypes.string,
          }),
          valueId: PropTypes.shape({
            keyName: PropTypes.string,
          }),
          valueText: PropTypes.string,
        }),
      ),
      licensorId: PropTypes.shape({
        name: PropTypes.string,
      }),
      licenseId: PropTypes.shape({
        name: PropTypes.string,
      }),
      likenessId: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ).isRequired,
  selectedCardAttributes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  colouredFaces: PropTypes.bool,
  handleSelection: PropTypes.func.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  handleCombinationSelection: PropTypes.func,
  selectedCombinations: PropTypes.arrayOf(PropTypes.string),
};

RenderProductInformationColouredCard.defaultProps = {
  selectedCardAttributes: [],
  colouredFaces: false,
  selectedIds: [],
  handleCombinationSelection: () => {},
  selectedCombinations: [],
};

export default RenderProductInformationColouredCard;
